import { useEffect, useRef } from "react";
import { toast } from "sonner";
import confetti from "canvas-confetti";

// Mensajes motivacionales por hitos
const MILESTONE_MESSAGES = {
  // Documentos
  documents: {
    1: { message: "¡Primer documento subido! 📄 Vas por buen camino", type: "success" },
    2: { message: "¡Genial! Ya tienes 2 de 3 documentos 🎯", type: "success" },
    3: { message: "¡Documentos completos! 🎉 Paso 2 terminado", type: "success", celebrate: true }
  },
  // DS-160
  ds160: {
    1: { message: "¡Empezaste el DS-160! 📝 Sigue así", type: "success" },
    3: { message: "¡25% del DS-160 completado! 💪", type: "success" },
    6: { message: "¡Mitad del DS-160 listo! 🚀 Ya falta menos", type: "success" },
    9: { message: "¡75% del DS-160! 🌟 Casi llegas", type: "success" },
    12: { message: "¡DS-160 completado! 🎊 ¡Excelente trabajo!", type: "success", celebrate: true }
  },
  // Simulaciones
  simulations: {
    1: { message: "¡Primera práctica de entrevista! 🎤 Sigue practicando", type: "success" },
    2: { message: "¡2 simulaciones! 💪 Estás cada vez más preparado", type: "success" },
    3: { message: "¡3 simulaciones! 🌟 ¡Eres un experto!", type: "success", celebrate: true }
  },
  // Pasos completados
  steps: {
    2: { message: "¡Paso 2 completado! 📁 Documentos listos", type: "success" },
    3: { message: "¡Paso 3 completado! 📋 DS-160 enviado", type: "success" },
    4: { message: "¡Paso 4 completado! 🎤 Listo para la entrevista", type: "success" },
    5: { message: "¡Cita agendada! 📅 Ya casi lo logras", type: "success", celebrate: true }
  }
};

// Función para lanzar confetti
const launchConfetti = () => {
  const count = 200;
  const defaults = {
    origin: { y: 0.7 },
    zIndex: 9999
  };

  function fire(particleRatio, opts) {
    confetti({
      ...defaults,
      ...opts,
      particleCount: Math.floor(count * particleRatio)
    });
  }

  fire(0.25, { spread: 26, startVelocity: 55 });
  fire(0.2, { spread: 60 });
  fire(0.35, { spread: 100, decay: 0.91, scalar: 0.8 });
  fire(0.1, { spread: 120, startVelocity: 25, decay: 0.92, scalar: 1.2 });
  fire(0.1, { spread: 120, startVelocity: 45 });
};

// Hook para detectar cambios y mostrar mensajes
export const useMotivationalMessages = (caseData, previousData) => {
  const prevDataRef = useRef(previousData);
  
  useEffect(() => {
    if (!caseData || !prevDataRef.current) {
      prevDataRef.current = caseData;
      return;
    }
    
    const prev = prevDataRef.current;
    
    // Detectar cambios en documentos
    const prevDocs = prev.documents_count || 0;
    const currDocs = caseData.documents_count || 0;
    if (currDocs > prevDocs && MILESTONE_MESSAGES.documents[currDocs]) {
      const milestone = MILESTONE_MESSAGES.documents[currDocs];
      toast.success(milestone.message, { duration: 4000 });
      if (milestone.celebrate) launchConfetti();
    }
    
    // Detectar cambios en DS-160
    const prevDS160 = prev.ds160_progress || 0;
    const currDS160 = caseData.ds160_progress || 0;
    if (currDS160 > prevDS160 && MILESTONE_MESSAGES.ds160[currDS160]) {
      const milestone = MILESTONE_MESSAGES.ds160[currDS160];
      toast.success(milestone.message, { duration: 4000 });
      if (milestone.celebrate) launchConfetti();
    }
    
    // Detectar cambios en simulaciones
    const prevSims = prev.simulations_count || 0;
    const currSims = caseData.simulations_count || 0;
    if (currSims > prevSims && MILESTONE_MESSAGES.simulations[currSims]) {
      const milestone = MILESTONE_MESSAGES.simulations[currSims];
      toast.success(milestone.message, { duration: 4000 });
      if (milestone.celebrate) launchConfetti();
    }
    
    // Detectar cambios en paso actual
    const prevStep = prev.current_step || 1;
    const currStep = caseData.current_step || 1;
    if (currStep > prevStep && MILESTONE_MESSAGES.steps[prevStep]) {
      const milestone = MILESTONE_MESSAGES.steps[prevStep];
      toast.success(milestone.message, { duration: 4000 });
      if (milestone.celebrate) launchConfetti();
    }
    
    prevDataRef.current = caseData;
  }, [caseData]);
};

// Componente para mostrar el progreso motivacional inline
export const ProgressMotivation = ({ documentsCount = 0, ds160Progress = 0, currentStep = 1 }) => {
  // Calcular mensaje motivacional basado en progreso general
  const getMotivationalMessage = () => {
    const totalProgress = (
      (Math.min(documentsCount, 3) / 3) * 0.2 +
      (Math.min(ds160Progress, 12) / 12) * 0.3 +
      ((currentStep - 1) / 5) * 0.5
    ) * 100;
    
    if (totalProgress >= 80) return "¡Casi lo logras! 🎯 Tu visa está cerca";
    if (totalProgress >= 60) return "¡Excelente progreso! 🚀 Sigue así";
    if (totalProgress >= 40) return "¡Vas muy bien! 💪 No te detengas";
    if (totalProgress >= 20) return "¡Buen inicio! 🌟 Continúa avanzando";
    return "¡Comienza tu camino! ✨ El éxito te espera";
  };
  
  return (
    <p className="text-sm text-navy-600 font-medium">
      {getMotivationalMessage()}
    </p>
  );
};

export default useMotivationalMessages;
